import React, { useCallback, useMemo, useState } from 'react';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { PageHeader } from '@components/molecules/PageHeader';
import { StatsCard } from '@components/molecules/StatsCard';
import { SearchInput } from '@components/molecules/SearchInput';
import GeofenceCategoriesGrid from '@components/organisms/GeofenceCategoriesGrid';
import GeofenceCategoryFormModal from '@components/organisms/GeofenceCategoryFormModal';
import {
  useGeofenceCategories,
  useGlobalGeofenceCategories,
  useCreateGeofenceCategory,
  useUpdateGeofenceCategory,
  useDeleteGeofenceCategory,
} from '@hooks/api/geofence';
import {
  GeofenceCategory,
  GeofenceCategoryCreateInput,
  GeofenceCategoryUpdateInput,
} from '@models/geofence.model';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircleOutline';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

const GeofenceCategoriesPage: React.FC = () => {
  const tActionMessages = useTranslation('actionMessages');
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<GeofenceCategory | null>(null);
  const [pendingDelete, setPendingDelete] = useState<GeofenceCategory | null>(null);

  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useGeofenceCategories();
  const { data: globalCategories = [] } = useGlobalGeofenceCategories();

  const createMutation = useCreateGeofenceCategory();
  const updateMutation = useUpdateGeofenceCategory();
  const deleteMutation = useDeleteGeofenceCategory();

  const isLoading = categoriesLoading && !categoriesError;

  const totalCount = categories.length;
  const activeCount = categories.filter((c) => c.isActive).length;
  const inactiveCount = totalCount - activeCount;

  const filteredCategories = useMemo(() => {
    let filtered = categories;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.description?.toLowerCase().includes(query),
      );
    }
    return [...filtered].sort((a, b) => {
      if (a.isGlobal && !b.isGlobal) return -1;
      if (!a.isGlobal && b.isGlobal) return 1;
      const sortDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      if (sortDiff !== 0) return sortDiff;
      return a.name.localeCompare(b.name);
    });
  }, [categories, searchQuery]);

  const handleOpenCreate = useCallback(() => {
    setEditing(null);
    setIsFormOpen(true);
  }, []);

  const handleOpenEdit = useCallback((category: GeofenceCategory) => {
    setEditing(category);
    setIsFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setEditing(null);
  }, []);

  const handleSubmit = useCallback(
    async (data: GeofenceCategoryCreateInput | GeofenceCategoryUpdateInput) => {
      try {
        if ('geofenceCategoryId' in data && data.geofenceCategoryId) {
          await updateMutation.mutateAsync(data as GeofenceCategoryUpdateInput);
          toast.success(tActionMessages.categoryUpdatedSuccessfully);
        } else {
          await createMutation.mutateAsync(data as GeofenceCategoryCreateInput);
          toast.success(tActionMessages.categoryCreatedSuccessfully);
        }
        handleCloseForm();
      } catch (err: any) {
        const message =
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          tActionMessages.failedToSaveCategory;
        toast.error(message);
      }
    },
    [
      createMutation,
      updateMutation,
      handleCloseForm,
      tActionMessages.categoryUpdatedSuccessfully,
      tActionMessages.categoryCreatedSuccessfully,
      tActionMessages.failedToSaveCategory,
    ],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await deleteMutation.mutateAsync(pendingDelete.geofenceCategoryId);
      toast.success(tActionMessages.categoryDeletedSuccessfully);
      setPendingDelete(null);
    } catch (err: any) {
      const message =
        err?.response?.errors?.[0]?.message || err?.message || tActionMessages.failedToDelete;
      toast.error(message);
    }
  }, [
    deleteMutation,
    pendingDelete,
    tActionMessages.categoryDeletedSuccessfully,
    tActionMessages.failedToDelete,
  ]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full p-12">
        <Loading size="3xl" />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Geofence Categories"
        backTo="/geofences"
        actions={
          <Button
            variant="primary"
            leftIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleOpenCreate}
          >
            Add Category
          </Button>
        }
      />

      <div className="grid grid-cols-3 gap-4">
        <StatsCard
          icon={<CategoryIcon className="text-gray-400" sx={{ fontSize: 24 }} />}
          value={totalCount}
          label="Total Categories"
        />
        <StatsCard
          icon={<CheckCircleIcon className="text-green-500" sx={{ fontSize: 24 }} />}
          value={activeCount}
          label="Active"
        />
        <StatsCard
          icon={<RemoveCircleIcon className="text-gray-400" sx={{ fontSize: 24 }} />}
          value={inactiveCount}
          label="Inactive"
        />
      </div>

      <SearchInput
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search categories..."
        className="w-64"
      />

      <GeofenceCategoriesGrid
        categories={filteredCategories}
        onEdit={handleOpenEdit}
        onDelete={(c) => setPendingDelete(c)}
        onAdd={handleOpenCreate}
      />

      <GeofenceCategoryFormModal
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        category={editing}
        isLoading={createMutation.isPending || updateMutation.isPending}
        globalCategories={globalCategories}
      />

      <ConfirmationModal
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        description={
          pendingDelete
            ? `Are you sure you want to delete "${pendingDelete.name}"? Geofences using it must be reassigned first.`
            : ''
        }
        confirmText="Delete"
        variant="danger"
      />
    </>
  );
};

export default GeofenceCategoriesPage;
